import { Box, Button, LinearProgress, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { t } from 'i18next';
import { useForm } from 'react-hook-form';
import { InputSelectFilterParamsField } from '../../../../components/v2/input/InputSelectFilterParamsField';
import { colors } from '../../../../theme/mui/colors';
import PlusIcon from '../../../../components/v2/icons/PlusIcon';
import InputSearch from '../../../../components/v2/input/InputSearch';
import { userAccessColumnsFn } from '../../../../components/v2/division/column/usersColumn';
import { MODAL_ID, MODALS_STORE } from '../../../../constants/ui/modal';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../auth/hooks';
import { UserAccess } from '../../../../api';
import { InputSelectField } from '../../../../components/v2/input/InputSelectField';
import { getUserAccessesAsync } from '../../../../api/users/query';
import InviteUserModal from 'modules/v2/servicesmodal/users/InviteUserModal';
import UpdateUserModal from '../../servicesmodal/users/UpdateUserModal';
import useAppNavigation from '../../../../hooks/general/useAppNavigation';
import { userOptions } from '../../../../constants/options/userOptions';
import DataGridTable from 'components/v2/table/DataGridTable';

const UserAdministration = () => {
  //#region Properties

  const theme = useTheme();
  const { updateQueryParams, getArrayParamsValue } = useAppNavigation();
  const isLgDown = useMediaQuery(theme.breakpoints.down('lg'));
  const { toggleModal, isModalOpen } = MODALS_STORE.userInvitation();
  const { isModalOpen: isUpdateUserModalOpened } = MODALS_STORE.userUpdate();

  const { user: authenticatedUser, userIsLoading, userIsFetched } = useAuth();
  const departments =
    authenticatedUser?.branches
      .filter(({ access }) => access === UserAccess.Admin)
      .map(({ name, id }) => ({
        value: id,
        label: name,
      })) ?? [];

  const defaultDepartmentId = useMemo(() => departments?.[0]?.value, [departments]);
  const {
    control: searchFormControl,
    watch,
    getValues,
  } = useForm({
    defaultValues: {
      departmentId: getArrayParamsValue('departmentId')?.[0] || defaultDepartmentId || '',
      statuses: getArrayParamsValue('status'),
      keyword: getArrayParamsValue('keyword')?.[0] ?? '',
    },
  });
  const departmentId = watch('departmentId');
  const watchedStatuses = watch('statuses');
  const [keyword, setKeyword] = useState(getArrayParamsValue('keyword')?.[0] || '');

  const {
    data: users,
    isLoading: isFetchingUsers,
    isRefetching: isRefetchingUsers,
    refetch: refetchUserAccesses,
  } = getUserAccessesAsync({
    branchId: getValues('departmentId'),
    userStatuses: getValues('statuses'),
    keyword: keyword,
  });

  const { columns: userAccessColumnTemplates, cancelInvitationRequestFn } = userAccessColumnsFn(() => departmentId);

  //#endregion

  //#region Life cycle

  useEffect(() => {
    updateQueryParams({
      departmentId: departmentId,
      status: watchedStatuses,
      keyword: keyword,
    });

    if (departmentId) {
      refetchUserAccesses();
    }
  }, [departmentId, watchedStatuses, keyword]);

  useEffect(() => {
    return () => {
      cancelInvitationRequestFn();
    };
  }, []);

  //#endregion

  //#region Methods

  function isLoading(): boolean {
    return userIsLoading || isFetchingUsers || isRefetchingUsers;
  }

  //#endregion

  return (
    <Stack>
      {isLoading() && <LinearProgress style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1101 }} />}
      <Stack marginBottom={'2rem'}>
        <Typography variant={'h4'} lineHeight={'120%'}>
          {t('userAdministration.manageUserAccess')}
        </Typography>
      </Stack>
      <form noValidate>
        <Stack direction={'row'} justifyContent={'space-between'} gap={'0.75rem 5.75rem'} flexWrap={'wrap-reverse'}>
          <Stack
            gap={'0.75rem'}
            flexDirection={'row'}
            flexWrap={isLgDown ? 'wrap' : 'unset'}
            flex={isLgDown ? '1 1 auto' : 'unset'}
          >
            <InputSelectField
              selectStyle={{ height: '2.5rem' }}
              style={{ width: isLgDown ? 'unset' : '12.75rem', flex: isLgDown ? 'auto' : 'unset' }}
              name="departmentId"
              control={searchFormControl}
              options={departments}
              label=""
              menuHeight={600}
              loading={!userIsFetched}
            ></InputSelectField>
            <InputSelectFilterParamsField
              name="statuses"
              style={{ width: isLgDown ? 'unset' : '7.25rem', flex: isLgDown ? 'auto' : 'unset' }}
              control={searchFormControl}
              options={userOptions}
              label={t('common.terms.status')}
              menuHeight={600}
              size="small"
              loading={!userIsFetched}
            />
            <Button
              style={{ color: colors.base.white, width: '9.9375rem', flex: isLgDown ? '1 1 auto' : 'unset' }}
              type="button"
              variant="contained"
              endIcon={<PlusIcon />}
              onClick={() => {
                toggleModal(MODAL_ID.USER_INVITATION);
              }}
            >
              {t('userAdministration.inviteNewUser')}
            </Button>
          </Stack>

          <Stack flex={isLgDown ? '1 1 auto' : '0 0 auto'} alignItems={'end'}>
            <InputSearch
              name="keyword"
              style={{ width: isLgDown ? '100%' : '21.25rem' }}
              onSearch={val => {
                setKeyword(val);
              }}
              placeholder={t('userAdministration.searchUsers')}
              size="small"
              disabled={!userIsFetched}
              defaultValue={keyword}
            />
          </Stack>
        </Stack>
      </form>
      {isModalOpen(MODAL_ID.USER_INVITATION) && <InviteUserModal />}
      {isUpdateUserModalOpened(MODAL_ID.USER_UPDATE) && <UpdateUserModal />}

      <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', marginBottom: '1rem', marginTop: '1.25rem' }}>
        <DataGridTable
          columns={userAccessColumnTemplates}
          rows={users || []}
          loading={isLoading()}
          hasNextPage={false}
        />
      </Box>
    </Stack>
  );
};

export default UserAdministration;
